import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../App';

export default function Staging() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.scroll-reveal');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? 'bg-[#1a1a1a] text-white' : 'bg-white text-[#000000]'
      } transition-colors duration-500`}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* CSS for scroll animations */}
      <style>{`
        .scroll-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal.animate-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .scroll-reveal-delay-1 { transition-delay: 0.1s; }
        .scroll-reveal-delay-2 { transition-delay: 0.2s; }
        .scroll-reveal-delay-3 { transition-delay: 0.3s; }
        .scroll-reveal-delay-4 { transition-delay: 0.4s; }
        .scroll-reveal-scale {
          opacity: 0;
          transform: scale(0.95);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal-scale.animate-visible {
          opacity: 1;
          transform: scale(1);
        }
        .scroll-reveal-left {
          opacity: 0;
          transform: translateX(-40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal-left.animate-visible {
          opacity: 1;
          transform: translateX(0);
        }
        .scroll-reveal-right {
          opacity: 0;
          transform: translateX(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal-right.animate-visible {
          opacity: 1;
          transform: translateX(0);
        }
      `}</style>

      {/* Top Right Icons */}
      <div className="fixed top-4 right-4 md:top-8 md:right-8 flex items-center gap-3 md:gap-4 z-50">
        <button
          onClick={toggleDarkMode}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${
            isDarkMode
              ? 'text-white/70 hover:text-white'
              : 'text-[#000000]/70 hover:text-[#000000]'
          } transition-colors cursor-pointer`}
        >
          <i className={`ri-${isDarkMode ? 'moon' : 'sun'}-line text-lg md:text-xl`}></i>
        </button>

        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${
            isDarkMode
              ? 'text-white/70 hover:text-white'
              : 'text-[#000000]/70 hover:text-[#000000]'
          } transition-colors cursor-pointer`}
        >
          <i className="ri-menu-line text-lg md:text-xl"></i>
        </button>
      </div>

      {/* Slide-out Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-72 md:w-80 ${
          isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
        } shadow-2xl transform transition-transform duration-500 z-40 ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-6 md:p-8">
          <button
            onClick={() => setMenuOpen(false)}
            className={`absolute top-6 right-6 md:top-8 md:right-8 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${
              isDarkMode
                ? 'text-white/70 hover:text-white'
                : 'text-[#000000]/70 hover:text-[#000000]'
            } transition-colors cursor-pointer`}
          >
            <i className="ri-close-line text-lg md:text-xl"></i>
          </button>

          <nav className="mt-16 md:mt-20 space-y-5 md:space-y-6">
            <Link
              to="/home"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text-white/90 hover:text-white'
                  : 'text-[#000000]/90 hover:text-[#000000]'
              } transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Home
            </Link>
            <a
              href="https://order.cinestate.ca/booking/new"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text-white/90 hover:text-white'
                  : 'text-[#000000]/90 hover:text-[#000000]'
              } transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Listing Packages
            </a>
            <Link
              to="/packages"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text-white/90 hover:text-white'
                  : 'text-[#000000]/90 hover:text-[#000000]'
              } transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Content Packages
            </Link>
            <Link
              to="/staging"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text-white/90 hover:text-white'
                  : 'text-[#000000]/90 hover:text-[#000000]'
              } transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Home Staging
            </Link>
            {/* Portfolio Dropdown */}
            <div>
              <button
                onClick={() => setPortfolioOpen(!portfolioOpen)}
                className={`flex items-center justify-between w-full text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors cursor-pointer`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <span>Portfolio</span>
                <i className={`${portfolioOpen ? 'ri-subtract-line' : 'ri-add-line'} text-xs`}></i>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${portfolioOpen ? 'max-h-24 mt-3' : 'max-h-0'}`}>
                <div className="ml-4 space-y-3">
                  <Link
                    to="/portfolio"
                    onClick={() => setMenuOpen(false)}
                    className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#000000]/55 hover:text-[#000000]'} transition-colors`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Residential
                  </Link>
                  <Link
                    to="/portfolio/commercial"
                    onClick={() => setMenuOpen(false)}
                    className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#000000]/55 hover:text-[#000000]'} transition-colors`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Commercial
                  </Link>
                </div>
              </div>
            </div>
            <Link
              to="/home#contact"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text-white/90 hover:text-white'
                  : 'text-[#000000]/90 hover:text-[#000000]'
              } transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Contact
            </Link>
          </nav>

          <div className="absolute bottom-8 md:bottom-12 left-6 md:left-8 flex gap-3 md:gap-4">
            <a
              href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border-white/20 text-white/70 hover:border-white hover:text-white'
                  : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'
              } transition-colors`}
            >
              <i className="ri-instagram-line text-sm md:text-base"></i>
            </a>
            <a
              href="https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border-white/20 text-white/70 hover:border-white hover:text-white'
                  : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'
              } transition-colors`}
            >
              <i className="ri-youtube-line text-sm md:text-base"></i>
            </a>
            <a
              href="https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border-white/20 text-white/70 hover:border-white hover:text-white'
                  : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'
              } transition-colors`}
            >
              <i className="ri-tiktok-line text-sm md:text-base"></i>
            </a>
            <a
              href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border-white/20 text-white/70 hover:border-white hover:text-white'
                  : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'
              } transition-colors`}
            >
              <i className="ri-facebook-fill text-sm md:text-base"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setMenuOpen(false)}
        ></div>
      )}

      {/* Logo - Top Left */}
      <div className="fixed top-4 left-4 md:top-8 md:left-8 z-40">
        <Link to="/" className="block">
          <img
            src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/91365b59b7e8e66ad4ab2980732fafc0.png"
            alt="CINÉSTATE"
            className={`h-5 md:h-7 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
          />
        </Link>
      </div>

      {/* Hero Section - Minimal */}
      <section className="min-h-screen flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="max-w-5xl mx-auto text-center w-full">
          <p
            className={`scroll-reveal text-[10px] sm:text-xs md:text-sm tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-4 sm:mb-6 md:mb-8 ${
              isDarkMode ? 'text-white/50' : 'text-[#000000]/50'
            }`}
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Home Staging
          </p>
          <h1
            className="scroll-reveal scroll-reveal-delay-1 text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-normal mb-4 sm:mb-6 md:mb-10 leading-[1.1] tracking-wide"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            TRANSFORMING SPACES<br className="hidden sm:block" />
            <span className="sm:hidden"> </span>INTO EXPERIENCES
          </h1>
          <p
            className={`scroll-reveal scroll-reveal-delay-2 text-sm sm:text-base md:text-xl lg:text-2xl max-w-2xl mx-auto leading-relaxed font-light px-2 ${
              isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Strategic interior design that maximizes property appeal and
            accelerates sales.
          </p>
        </div>
      </section>

      {/* Bundle Offer Section */}
      <section
        id="bundle-offer"
        className={`py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-8 lg:px-16 ${
          isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
        }`}
      >
        <div className="max-w-4xl mx-auto text-center">
          <p
            className="scroll-reveal text-[10px] md:text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-3 sm:mb-4 md:mb-6"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Exclusive Bundle Offer
          </p>
          <h2
            className="scroll-reveal scroll-reveal-delay-1 text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal mb-3 sm:mb-4 md:mb-6 tracking-wide px-2"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Home Staging + Complete Media Package
          </h2>
          <p
            className={`scroll-reveal scroll-reveal-delay-2 text-xs sm:text-sm md:text-lg mb-6 sm:mb-8 md:mb-10 leading-relaxed font-light max-w-2xl mx-auto px-2 ${
              isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Combine our professional staging services with any listing package and
            receive premium photography absolutely free, plus one complimentary
            virtual twilight image.
          </p>
          <div
            className={`scroll-reveal scroll-reveal-delay-3 inline-block px-5 py-3 sm:px-6 sm:py-4 md:px-10 md:py-6 border ${
              isDarkMode ? 'border-white/20' : 'border-[#000000]/20'
            }`}
          >
            <p
              className="text-[9px] sm:text-[10px] md:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase mb-1 sm:mb-2"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Premium Photography + Virtual Twilight
            </p>
            <p className="text-base sm:text-lg md:text-2xl font-normal" style={{ fontFamily: "'Inter', sans-serif" }}>
              COMPLIMENTARY
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-14 sm:py-20 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <h2
            className="scroll-reveal text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal text-center mb-10 sm:mb-16 md:mb-24 tracking-wide"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            STAGING SOLUTIONS
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-12 md:gap-16 lg:gap-20">
            {/* Full Service */}
            <div className="scroll-reveal text-center sm:col-span-2 lg:col-span-1">
              <div
                className={`w-12 h-12 sm:w-14 sm:h-14 md:w-20 md:h-20 mx-auto mb-4 sm:mb-6 md:mb-10 flex items-center justify-center border ${
                  isDarkMode ? 'border-white/20' : 'border-[#000000]/20'
                }`}
              >
                <i className="ri-home-4-line text-xl sm:text-2xl md:text-3xl"></i>
              </div>
              <h3 className="text-sm sm:text-base md:text-xl font-normal mb-3 sm:mb-4 md:mb-6 tracking-wide" style={{ fontFamily: "'Inter', sans-serif" }}>
                FULL SERVICE
              </h3>
              <p
                className={`text-xs sm:text-sm md:text-base leading-relaxed font-light max-w-xs mx-auto ${
                  isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Complete property transformation from empty space to market-ready
                masterpiece. We design and furnish every room to showcase the
                property's full potential.
              </p>
            </div>

            {/* Partial Staging */}
            <div className="scroll-reveal scroll-reveal-delay-1 text-center">
              <div
                className={`w-12 h-12 sm:w-14 sm:h-14 md:w-20 md:h-20 mx-auto mb-4 sm:mb-6 md:mb-10 flex items-center justify-center border ${
                  isDarkMode ? 'border-white/20' : 'border-[#000000]/20'
                }`}
              >
                <i className="ri-layout-line text-xl sm:text-2xl md:text-3xl"></i>
              </div>
              <h3 className="text-sm sm:text-base md:text-xl font-normal mb-3 sm:mb-4 md:mb-6 tracking-wide" style={{ fontFamily: "'Inter', sans-serif" }}>
                PARTIAL STAGING
              </h3>
              <p
                className={`text-xs sm:text-sm md:text-base leading-relaxed font-light max-w-xs mx-auto ${
                  isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Seamless integration of our premium inventory with your existing
                furniture. We enhance what you have and fill the gaps to create a
                cohesive, elevated look.
              </p>
            </div>

            {/* Styling Services */}
            <div className="scroll-reveal scroll-reveal-delay-2 text-center">
              <div
                className={`w-12 h-12 sm:w-14 sm:h-14 md:w-20 md:h-20 mx-auto mb-4 sm:mb-6 md:mb-10 flex items-center justify-center border ${
                  isDarkMode ? 'border-white/20' : 'border-[#000000]/20'
                }`}
              >
                <i className="ri-magic-line text-xl sm:text-2xl md:text-3xl"></i>
              </div>
              <h3 className="text-sm sm:text-base md:text-xl font-normal mb-3 sm:mb-4 md:mb-6 tracking-wide" style={{ fontFamily: "'Inter', sans-serif" }}>
                STYLING
              </h3>
              <p
                className={`text-xs sm:text-sm md:text-base leading-relaxed font-light max-w-xs mx-auto ${
                  isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Strategic redesign using your current furnishings. We reimagine
                your space through expert placement, decluttering, and styling to
                maximize visual impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className={`w-full h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'}`}></div>

      {/* How to Get Started */}
      <section id="get-started" className="py-14 sm:py-20 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <h2
            className="scroll-reveal text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal text-center mb-10 sm:mb-16 md:mb-24 tracking-wide"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            HOW TO GET STARTED
          </h2>

          <div className="space-y-8 sm:space-y-12 md:space-y-20">
            <div className="scroll-reveal-left scroll-reveal flex items-start gap-4 sm:gap-6 md:gap-10">
              <div
                className={`text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-light ${
                  isDarkMode ? 'text-white/10' : 'text-[#000000]/10'
                } flex-shrink-0 leading-none`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                01
              </div>
              <div className="pt-1 sm:pt-2 md:pt-4">
                <h3 className="text-sm sm:text-base md:text-xl font-normal mb-2 sm:mb-3 md:mb-4 tracking-wide" style={{ fontFamily: "'Inter', sans-serif" }}>
                  RECORD A WALKTHROUGH
                </h3>
                <p
                  className={`text-xs sm:text-sm md:text-base leading-relaxed font-light ${
                    isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Use your smartphone to capture a simple video tour of the
                  property. Show us each room, the current condition, and any
                  areas of concern.
                </p>
              </div>
            </div>

            <div className="scroll-reveal-right scroll-reveal flex items-start gap-4 sm:gap-6 md:gap-10">
              <div
                className={`text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-light ${
                  isDarkMode ? 'text-white/10' : 'text-[#000000]/10'
                } flex-shrink-0 leading-none`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                02
              </div>
              <div className="pt-1 sm:pt-2 md:pt-4">
                <h3 className="text-sm sm:text-base md:text-xl font-normal mb-2 sm:mb-3 md:mb-4 tracking-wide" style={{ fontFamily: "'Inter', sans-serif" }}>
                  SEND IT TO US
                </h3>
                <p
                  className={`text-xs sm:text-sm md:text-base leading-relaxed font-light mb-3 sm:mb-4 ${
                    isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Email your walkthrough video to us at:
                </p>
                <a
                  href="mailto:info@cinestate.ca?subject=Home%20Staging%20Quote%20Request"
                  className={`text-sm sm:text-base md:text-lg font-normal ${
                    isDarkMode ? 'text-white hover:text-white/80' : 'text-[#000000] hover:text-[#000000]/80'
                  } transition-colors break-all sm:break-normal`}
                >
                  info@cinestate.ca
                </a>
              </div>
            </div>

            <div className="scroll-reveal-left scroll-reveal flex items-start gap-4 sm:gap-6 md:gap-10">
              <div
                className={`text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-light ${
                  isDarkMode ? 'text-white/10' : 'text-[#000000]/10'
                } flex-shrink-0 leading-none`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                03
              </div>
              <div className="pt-1 sm:pt-2 md:pt-4">
                <h3 className="text-sm sm:text-base md:text-xl font-normal mb-2 sm:mb-3 md:mb-4 tracking-wide" style={{ fontFamily: "'Inter', sans-serif" }}>
                  RECEIVE YOUR PROPOSAL
                </h3>
                <p
                  className={`text-xs sm:text-sm md:text-base leading-relaxed font-light ${
                    isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  We'll analyze the space and provide you with a comprehensive
                  staging plan and transparent pricing tailored to your
                  property.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`border-t ${isDarkMode ? 'border-white/10' : 'border-[#000000]/10'}`}>
        <div className="px-4 sm:px-6 md:px-8 lg:px-16 py-8 sm:py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col gap-6 sm:gap-8 mb-6 sm:mb-8 md:mb-12">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
                <Link to="/" className="block">
                  <img
                    src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/91365b59b7e8e66ad4ab2980732fafc0.png"
                    alt="CINÉSTATE"
                    className={`h-4 sm:h-5 md:h-7 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
                  />
                </Link>

                <div className="flex gap-2 sm:gap-3 md:gap-4">
                  <a
                    href="https://order.kianikanstudio.ca/booking/new"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}
                  >
                    <i className="ri-instagram-line text-sm md:text-base"></i>
                  </a>
                  <a
                    href="https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}
                  >
                    <i className="ri-youtube-line text-sm md:text-base"></i>
                  </a>
                  <a
                    href="https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}
                  >
                    <i className="ri-tiktok-line text-sm md:text-base"></i>
                  </a>
                  <a
                    href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}
                  >
                    <i className="ri-facebook-fill text-sm md:text-base"></i>
                  </a>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 sm:gap-5 md:gap-8">
                <a
                  href="https://order.cinestate.ca/booking/new"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${
                    isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'
                  } transition-colors font-normal`}
                >
                  Listing Packages
                </a>
                <Link
                  to="/packages"
                  className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${
                    isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'
                  } transition-colors font-normal`}
                >
                  Content Packages
                </Link>
                <Link
                  to="/staging"
                  className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${
                    isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'
                  } transition-colors font-normal`}
                >
                  Home Staging
                </Link>
                <Link
                  to="/portfolio"
                  className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${
                    isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'
                  } transition-colors font-normal`}
                >
                  Portfolio
                </Link>
                <Link
                  to="/home#contact"
                  className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${
                    isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'
                  } transition-colors font-normal`}
                >
                  Contact
                </Link>
              </div>
            </div>

            <div className={`w-full h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'} mb-4 sm:mb-6 md:mb-8`}></div>

            <div
              className={`flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-3 md:gap-4 text-[10px] sm:text-xs md:text-sm ${
                isDarkMode ? 'text-white/50' : 'text-[#000000]/50'
              }`}
            >
              <p className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                © 2025 Cinéstate. All rights reserved.
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                className={`${isDarkMode ? 'hover:text-white' : 'hover:text-[#000000]'} transition-colors font-light`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
